import { useLocation } from "@reach/router";
import { getTime } from "date-fns";
import { formatInTimeZone } from "date-fns-tz";
import enIN from "date-fns/locale/en-IN";
import { useMemo } from "react";
import registrationOnlyMode from "./registrationOnlyMode";
import { useEventContext } from "../context/EventProvider";

const useEventDetails = () => {
  const { event } = useEventContext();
  const eventId = event?.eventId;
  const starts = event?.starts;
  const ends = event?.ends;
  const externalRegistrationEmbedId = event?.externalRegistrationEmbedId;
  const externalEventPlatform = event?.externalEventPlatform;
  const stripePK = process.env.GATSBY_STRIPE_EMBED_PK || '';
  const checkoutUrl = `${process.env.GATSBY_STRIPE_CHECKOUT_SESSION_URI}/create-checkout-session`;
  const cmsPrefix = process.env.STRAPI_API_URL;
  const timeZone = process.env.EVENT_TIME_ZONE_NAME || "GMT";
  const { pathname } = useLocation();
  const registrationOnly = registrationOnlyMode();
  const virtual = pathname.includes("virtual");
  const agenda = pathname.includes("agenda");
  const techExpo = pathname.includes("tech-expo");
  const startDate = new Date(starts || Date.now());
  const endDate = new Date(ends || Date.now());
  const todayTime = getTime(new Date());
  const eventStartTime = getTime(startDate);
  const eventEndTime = getTime(endDate);
  const formattedStartDate = formatInTimeZone(startDate, timeZone, "M.d.yy", {
    locale: enIN,
  });
  const formattedStartTime = formatInTimeZone(startDate, timeZone, "h:mma", {
    locale: enIN,
  });
  const formattedEndTime = formatInTimeZone(endDate, timeZone, "h:mma", {
    locale: enIN,
  });
  const formattedTimeZone = formatInTimeZone(startDate, timeZone, "zz");
  const isAfterEvent = useMemo(() => Date.now() > eventEndTime, [eventEndTime]);
  const isBeforeEvent = useMemo(
    () => eventStartTime > Date.now(),
    [eventStartTime]
  );
  const isDuringEvent = useMemo(
    () => !isBeforeEvent && !isAfterEvent,
    [isBeforeEvent, isAfterEvent]
  );
  const showLogin = virtual && !registrationOnly;
  const usDateToMachineDate = (dateStr: string): Date => {
    let [month, day, year] = dateStr
      .split("/")
      .map((num) => (num.length === 1 ? "0" + num : num));
    return new Date(`${year}-${month}-${day}T00:00:00Z`);
  };

  return {
    cmsPrefix,
    agenda,
    virtual,
    startDate,
    endDate,
    eventId,
    externalRegistrationEmbedId,
    externalEventPlatform,
    formattedStartDate,
    formattedStartTime,
    formattedEndTime,
    formattedTimeZone,
    techExpo,
    todayTime,
    eventStartTime,
    eventEndTime,
    isBeforeEvent,
    isDuringEvent,
    isAfterEvent,
    registrationOnly,
    showLogin,
    usDateToMachineDate,
    stripePK,
    checkoutUrl,
  };
};

export default useEventDetails;
